<template>
  <main class="home-hl">
    <!-- 1. Hero: brand + one headline + one line + CTA -->
    <section class="hero">
      <div class="hero-bg" aria-hidden="true">
        <div class="hero-bg-glow"></div>
        <div class="hero-bg-grid"></div>
        <div class="hero-bg-mark"></div>
      </div>

      <div class="hero-inner">
        <img class="hero-mark animate-in" :src="brandLogo" alt="HamiltonLane" />
        <h1 class="hero-headline animate-in delay-1">{{ $t('home.titleLine2') }}</h1>
        <p class="hero-lead animate-in delay-2">{{ $t('home.mainDescription') }}</p>
        <div class="hero-cta animate-in delay-3" :class="{ solo: isWalletConnected }">
          <button v-if="!isWalletConnected" type="button" class="btn-primary" @click="handleStartJoining">
            {{ $t('home.startJoining') }}
          </button>
          <button type="button" class="btn-ghost" @click="openTradingBotsLink">
            {{ $t('home.tradingBotsLink') }}
          </button>
        </div>
        <p class="hero-status animate-in delay-4">
          <span class="status-dot" :class="{ on: isWalletConnected }"></span>
          {{ isWalletConnected ? $t('home.walletConnected') : $t('home.walletNotConnected') }}
          <span class="status-sep">·</span>
          {{ $t('home.heroMarketTag') }}
        </p>
      </div>
    </section>

    <!-- 2. Four principles -->
    <section class="band principles">
      <article v-for="item in companyHighlights" :key="item.title" class="principle">
        <h3>{{ item.title }}</h3>
        <p>{{ item.desc }}</p>
      </article>
    </section>

    <!-- 3. Solutions -->
    <section class="band solutions">
      <header class="band-head">
        <h2>{{ $t('home.investmentTitle') }}</h2>
        <p>{{ $t('home.investmentSubtitle') }}</p>
      </header>
      <div class="solution-list">
        <article
          v-for="(category, index) in categories"
          :key="category.title"
          class="solution"
        >
          <span class="solution-num">{{ String(index + 1).padStart(2, '0') }}</span>
          <div class="solution-main">
            <h3>{{ category.title }}</h3>
            <p>
              <span :class="{ clamp: !expandedItems[index] }">
                {{ expandedItems[index] ? category.full : category.short }}
              </span>
              <button type="button" class="linkish" @click="toggleExpand(index)">
                {{ expandedItems[index] ? $t('home.collapse') : $t('home.more') }}
              </button>
            </p>
          </div>
        </article>
      </div>
    </section>

    <!-- 4. Performance -->
    <section class="band performance">
      <header class="band-head">
        <h2>{{ $t('home.earningsTitle') }}</h2>
        <p>{{ $t('home.earningsSubtitle') }}</p>
      </header>
      <div class="board">
        <div class="board-head">
          <span>#</span>
          <span>{{ $t('home.walletAddressLabel') }}</span>
          <span>{{ $t('home.profitLabel') }}</span>
        </div>
        <div
          v-for="(item, index) in visibleEarnings"
          :key="`${item.address}-${index}`"
          class="board-row"
        >
          <span class="rank">{{ index + 1 }}</span>
          <span class="addr">{{ item.address }}</span>
          <span class="profit">{{ item.profit }}</span>
        </div>
      </div>
    </section>

    <!-- 5. About -->
    <section class="band about">
      <header class="band-head">
        <h2>{{ $t('home.companyTitleHB') }}</h2>
        <p>{{ $t('home.heroIntro') }}</p>
      </header>

      <div class="timeline">
        <article v-for="item in companyTimeline" :key="item.year" class="timeline-step">
          <span class="timeline-label">{{ item.year }}</span>
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
        </article>
      </div>

      <div class="about-copy">
        <template v-if="companyExpanded">
          <p v-for="(paragraph, idx) in companyParagraphs" :key="`p-${idx}`">{{ paragraph }}</p>
        </template>
        <p v-else>{{ $t('home.companyShortHB') }}</p>
        <button type="button" class="linkish" @click="toggleCompanyDescription">
          {{ companyExpanded ? $t('home.collapse') : $t('home.more') }}
        </button>
      </div>

      <div class="about-actions">
        <button type="button" class="btn-ghost" @click="openFoundationLink">
          {{ $t('home.companyFoundationLink') }}
        </button>
        <button type="button" class="btn-ghost" @click="openHamiltonLaneDocsLink">
          {{ $t('home.companyDocsLink') }}
        </button>
      </div>
    </section>

    <!-- 6. FAQ -->
    <section class="band faq">
      <header class="band-head">
        <h2>{{ $t('home.faqTitle') }}</h2>
      </header>
      <div class="faq-list">
        <div v-for="(faq, index) in faqList" :key="`faq-${index}`" class="faq-item">
          <button type="button" class="faq-q" @click="toggleFaq(index)">
            <span>{{ faq.question }}</span>
            <span class="faq-icon" :class="{ open: faq.isOpen }" aria-hidden="true"></span>
          </button>
          <p v-if="faq.isOpen" class="faq-a">{{ faq.answer }}</p>
        </div>
      </div>
    </section>

    <!-- 7. Partners -->
    <section class="band partners">
      <header class="band-head">
        <h2>{{ $t('home.partnerTitle') }}</h2>
      </header>
      <div class="partner-row">
        <div v-for="(platform, index) in platforms" :key="`p-${index}`" class="partner">
          <PlatformIcon :name="platform.iconName" :size="16" />
          <span>{{ platform.name }}</span>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import PlatformIcon from '@/components/icons/PlatformIcon.vue'
import userStore from '@/utils/userStore'
import { API_CONFIG } from '@/config/api.js'
import brandLogo from '@/assets/images/hamilton-lane-logo-transparent.png'

export default {
  name: 'Home',
  components: { PlatformIcon },
  setup() {
    const { t } = useI18n()
    const isWalletConnected = ref(false)

    const checkWalletConnection = () => {
      isWalletConnected.value = userStore.isLoggedIn()
    }

    const onUserConnected = () => checkWalletConnection()

    const officialWebsiteUrl = ref('')
    const foundationUrl = ref('')
    const hummingbotDocsUrl = ref('')

    const openFoundationLink = () => {
      window.location.href = foundationUrl.value || 'https://www.hamiltonlane.com/'
    }
    const openHamiltonLaneDocsLink = () => {
      window.location.href = hummingbotDocsUrl.value || 'https://www.hamiltonlane.com/'
    }
    const openTradingBotsLink = () => {
      window.location.href = officialWebsiteUrl.value || 'https://www.hamiltonlane.com/'
    }

    const loadSystemConfig = async () => {
      try {
        const controller = new AbortController()
        const timer = setTimeout(() => controller.abort(), 5000)
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/system/config`, {
          headers: { 'X-Tenant-Domain': window.location.hostname },
          signal: controller.signal
        })
        clearTimeout(timer)
        const data = await response.json()
        if (data.success && data.data) {
          const s = data.data.system || {}
          if (s.official_website_url) officialWebsiteUrl.value = s.official_website_url
          if (s.foundation_url) foundationUrl.value = s.foundation_url
          if (s.hummingbot_docs_url) hummingbotDocsUrl.value = s.hummingbot_docs_url
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error('加载系统配置失败:', error)
      }
    }

    const handleStartJoining = () => {
      window.dispatchEvent(new CustomEvent('start-wallet-authorization'))
    }

    const expandedItems = ref({})
    const toggleExpand = (index) => {
      expandedItems.value[index] = !expandedItems.value[index]
    }

    const companyExpanded = ref(false)
    const toggleCompanyDescription = () => {
      companyExpanded.value = !companyExpanded.value
    }

    const earningsData = ref([
      { address: '0xBC607……147413B4', profit: '+72827.34USDC' },
      { address: '0x782BF……6DEF6A52', profit: '+64796.31USDC' },
      { address: '0xA8803……568A9A1D', profit: '+271603.84USDC' },
      { address: '0xE417E……DEA4C726', profit: '+303328.96USDC' },
      { address: '0x347FF……BF4F4177', profit: '+267771.73USDC' },
      { address: '0x3D298……3F31BEC1', profit: '+44201.68USDC' },
      { address: '0xC73B7……CFFEDA71', profit: '+44485.26USDC' },
      { address: '0x70F28……A59C95CE', profit: '+49772.84USDC' },
      { address: '0xC0BB6……D0B48024', profit: '+39047.64USDC' },
      { address: '0x762F2……F8C4CC5C', profit: '+193015.85USDC' }
    ])
    const leaderboardStart = ref(0)
    let leaderboardTimer = null

    const visibleEarnings = computed(() => {
      const rows = 6
      return Array.from({ length: rows }).map((_, idx) => {
        const dataIndex = (leaderboardStart.value + idx) % earningsData.value.length
        return earningsData.value[dataIndex]
      })
    })

    const faqOpenStates = ref([false, false, false])
    const faqList = computed(() => [
      { question: t('home.faq1Question'), answer: t('home.faq1Answer'), isOpen: faqOpenStates.value[0] },
      { question: t('home.faq2Question'), answer: t('home.faq2Answer'), isOpen: faqOpenStates.value[1] },
      { question: t('home.faq3Question'), answer: t('home.faq3Answer'), isOpen: faqOpenStates.value[2] }
    ])

    const platforms = ref([
      { name: 'Crypto.com', iconName: 'crypto' },
      { name: 'Binance', iconName: 'binance' },
      { name: 'OKX', iconName: 'okx' },
      { name: 'Bitget', iconName: 'bitget' },
      { name: 'LBank', iconName: 'lbank' },
      { name: 'KuCoin', iconName: 'kucoin' },
      { name: 'Kraken', iconName: 'kraken' },
      { name: 'Gate.io', iconName: 'gate' },
      { name: 'Upbit', iconName: 'upbit' },
      { name: 'Bitfinex', iconName: 'bitfinex' },
      { name: 'WazirX', iconName: 'wazirx' },
      { name: 'HTX', iconName: 'huobi' },
      { name: 'Coinbase', iconName: 'coinbase' }
    ])

    const splitCompanyIntoParagraphs = (text) => {
      const normalized = String(text || '').replace(/\s+/g, ' ').trim()
      if (!normalized) return []
      if (normalized.length <= 240) return [normalized]
      const totalParts = 3
      const targetSize = Math.ceil(normalized.length / totalParts)
      const punctuation = /[。！？.!?；;]/
      const blocks = []
      let start = 0
      for (let i = 0; i < totalParts - 1; i++) {
        const ideal = start + targetSize
        let splitAt = -1
        const leftBound = Math.max(start + 80, ideal - 90)
        const rightBound = Math.min(normalized.length - 1, ideal + 120)
        for (let j = leftBound; j <= rightBound; j++) {
          if (punctuation.test(normalized[j])) splitAt = j + 1
        }
        if (splitAt === -1) splitAt = Math.min(ideal, normalized.length)
        const chunk = normalized.slice(start, splitAt).trim()
        if (chunk) blocks.push(chunk)
        start = splitAt
      }
      const tail = normalized.slice(start).trim()
      if (tail) blocks.push(tail)
      return blocks
    }

    const companyParagraphs = computed(() => splitCompanyIntoParagraphs(t('home.companyFullHB')))
    const companyHighlights = computed(() => ([
      { title: t('home.companyHighlight1Title'), desc: t('home.companyHighlight1Desc') },
      { title: t('home.companyHighlight2Title'), desc: t('home.companyHighlight2Desc') },
      { title: t('home.companyHighlight3Title'), desc: t('home.companyHighlight3Desc') },
      { title: t('home.companyHighlight4Title'), desc: t('home.companyHighlight4Desc') }
    ]))
    const companyTimeline = computed(() => ([
      { year: t('home.companyTimeline1Year'), title: t('home.companyTimeline1Title'), desc: t('home.companyTimeline1Desc') },
      { year: t('home.companyTimeline2Year'), title: t('home.companyTimeline2Title'), desc: t('home.companyTimeline2Desc') },
      { year: t('home.companyTimeline3Year'), title: t('home.companyTimeline3Title'), desc: t('home.companyTimeline3Desc') }
    ]))
    const categories = computed(() => [
      { title: t('home.category1Title'), short: t('home.category1Short'), full: t('home.category1Full') },
      { title: t('home.category2Title'), short: t('home.category2Short'), full: t('home.category2Full') },
      { title: t('home.category3Title'), short: t('home.category3Short'), full: t('home.category3Full') },
      { title: t('home.category4Title'), short: t('home.category4Short'), full: t('home.category4Full') },
      { title: t('home.category5Title'), short: t('home.category5Short'), full: t('home.category5Full') },
      { title: t('home.category6Title'), short: t('home.category6Short'), full: t('home.category6Full') },
      { title: t('home.category7Title'), short: t('home.category7Short'), full: t('home.category7Full') }
    ])

    const toggleFaq = (index) => {
      faqOpenStates.value[index] = !faqOpenStates.value[index]
    }

    onMounted(() => {
      checkWalletConnection()
      loadSystemConfig()
      window.addEventListener('user-connected', onUserConnected)
      leaderboardTimer = setInterval(() => {
        leaderboardStart.value = (leaderboardStart.value + 1) % earningsData.value.length
      }, 2200)
    })

    onUnmounted(() => {
      window.removeEventListener('user-connected', onUserConnected)
      if (leaderboardTimer) clearInterval(leaderboardTimer)
    })

    return {
      brandLogo,
      isWalletConnected,
      handleStartJoining,
      openFoundationLink,
      openHamiltonLaneDocsLink,
      openTradingBotsLink,
      expandedItems,
      toggleExpand,
      companyExpanded,
      companyParagraphs,
      companyHighlights,
      companyTimeline,
      toggleCompanyDescription,
      faqList,
      toggleFaq,
      platforms,
      categories,
      visibleEarnings
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=Libre+Franklin:wght@500;600;700&display=swap');

.home-hl {
  --ink: #f3f7fb;
  --muted: #94a8bb;
  --dim: #6f8498;
  --blue: #1b75bc;
  --blue-2: #1590d0;
  --line: rgba(170, 198, 220, 0.14);
  --panel: #0a121b;
  --gain: #3ecf8e;
  --radius-motif: 0 0 0 18px;
  min-height: 100vh;
  padding: 64px 0 96px;
  color: var(--ink);
  font-family: 'DM Sans', 'PingFang SC', 'Noto Sans SC', sans-serif;
  background: #05090e;
}

.animate-in {
  animation: rise 0.85s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.delay-1 { animation-delay: 0.1s; }
.delay-2 { animation-delay: 0.2s; }
.delay-3 { animation-delay: 0.3s; }
.delay-4 { animation-delay: 0.42s; }

@keyframes rise {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}

/* —— Hero —— */
.hero {
  position: relative;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  border-bottom: 1px solid var(--line);
}

.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hero-bg-glow {
  position: absolute;
  inset: -20% -10% auto;
  height: 70%;
  background:
    radial-gradient(ellipse 55% 70% at 18% 40%, rgba(27, 117, 188, 0.38), transparent 70%),
    radial-gradient(ellipse 45% 55% at 88% 20%, rgba(21, 144, 208, 0.18), transparent 65%);
  animation: glowShift 12s ease-in-out infinite alternate;
}

@keyframes glowShift {
  from { transform: translate3d(0, 0, 0) scale(1); }
  to { transform: translate3d(2%, 3%, 0) scale(1.04); }
}

.hero-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
  background-size: 56px 56px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.55), transparent 85%);
  animation: gridDrift 28s linear infinite;
}

@keyframes gridDrift {
  from { background-position: 0 0, 0 0; }
  to { background-position: 56px 56px, 56px 56px; }
}

.hero-bg-mark {
  position: absolute;
  right: -8%;
  bottom: -18%;
  width: min(70vw, 520px);
  aspect-ratio: 1;
  border-radius: var(--radius-motif);
  background: linear-gradient(145deg, rgba(27, 117, 188, 0.22), rgba(27, 117, 188, 0.02) 62%);
  border: 1px solid rgba(27, 117, 188, 0.18);
  transform: rotate(-8deg);
  animation: markFloat 9s ease-in-out infinite;
}

@keyframes markFloat {
  0%, 100% { transform: rotate(-8deg) translateY(0); }
  50% { transform: rotate(-6deg) translateY(-10px); }
}

.hero-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 40px 20px 32px;
}

.hero-mark {
  display: block;
  width: min(72%, 260px);
  height: auto;
  margin-bottom: 18px;
  filter: drop-shadow(0 12px 40px rgba(0, 0, 0, 0.45));
}

.hero-headline {
  margin: 0;
  max-width: 14ch;
  font-family: 'Libre Franklin', 'DM Sans', sans-serif;
  font-size: clamp(28px, 7.2vw, 44px);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.03em;
  color: #fff;
}

.hero-lead {
  margin: 12px 0 0;
  max-width: 36rem;
  font-size: 14px;
  line-height: 1.7;
  color: var(--muted);
}

.hero-cta {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.hero-cta.solo .btn-ghost {
  min-width: 160px;
}

.btn-primary,
.btn-ghost {
  height: 46px;
  padding: 0 22px;
  border: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: transform 0.2s ease, filter 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  border-radius: var(--radius-motif);
}

.btn-primary {
  background: linear-gradient(135deg, var(--blue-2), var(--blue));
  color: #fff;
}

.btn-primary:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.btn-ghost {
  background: transparent;
  color: var(--ink);
  border: 1px solid rgba(186, 210, 228, 0.28);
}

.btn-ghost:hover {
  border-color: rgba(27, 117, 188, 0.7);
  background: rgba(27, 117, 188, 0.08);
}

.hero-status {
  margin: 16px 0 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dim);
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5a6f84;
}

.status-dot.on {
  background: var(--gain);
  box-shadow: 0 0 0 3px rgba(62, 207, 142, 0.18);
}

.status-sep {
  opacity: 0.5;
}

/* —— Bands —— */
.band {
  max-width: 1120px;
  margin: 0 auto;
  padding: 40px 20px;
  border-bottom: 1px solid var(--line);
}

.band-head h2 {
  margin: 0;
  font-family: 'Libre Franklin', sans-serif;
  font-size: clamp(22px, 4.5vw, 30px);
  font-weight: 700;
  letter-spacing: -0.02em;
}

.band-head p {
  margin: 8px 0 0;
  max-width: 40rem;
  font-size: 13px;
  line-height: 1.65;
  color: var(--muted);
}

/* —— Principles —— */
.principles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.principle {
  padding: 22px 16px;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.principle:nth-child(2n) {
  border-right: none;
}

.principle h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
}

.principle p {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.55;
  color: var(--muted);
}

/* —— Solutions —— */
.solution-list {
  margin-top: 22px;
  display: grid;
  gap: 0;
  border-top: 1px solid var(--line);
}

.solution {
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 10px;
  padding: 18px 2px;
  border-bottom: 1px solid var(--line);
  transition: background 0.2s ease;
}

.solution:hover {
  background: rgba(27, 117, 188, 0.05);
}

.solution-num {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--blue);
  padding-top: 2px;
}

.solution-main h3 {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
}

.solution-main p {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: var(--muted);
}

.clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.linkish {
  border: none;
  background: none;
  color: #6eb4df;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
}

/* —— Board —— */
.board {
  margin-top: 18px;
  border: 1px solid var(--line);
  border-radius: var(--radius-motif);
  overflow: hidden;
  background: rgba(10, 18, 27, 0.85);
}

.board-head,
.board-row {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 0 14px;
}

.board-head {
  min-height: 38px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--dim);
  background: rgba(27, 117, 188, 0.1);
  border-bottom: 1px solid var(--line);
}

.board-row {
  min-height: 48px;
  border-top: 1px solid rgba(170, 198, 220, 0.08);
}

.board-row:hover {
  background: rgba(27, 117, 188, 0.06);
}

.rank {
  font-size: 12px;
  font-weight: 700;
  color: var(--blue);
}

.addr {
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #a7bbcc;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profit {
  font-size: 13px;
  font-weight: 700;
  color: var(--gain);
}

/* —— About —— */
.timeline {
  margin-top: 22px;
  display: grid;
  gap: 14px;
}

.timeline-step {
  padding-left: 14px;
  border-left: 2px solid rgba(27, 117, 188, 0.55);
}

.timeline-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--blue);
}

.timeline-step h3 {
  margin: 4px 0 0;
  font-size: 15px;
}

.timeline-step p {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.55;
  color: var(--muted);
}

.about-copy {
  margin-top: 22px;
}

.about-copy p {
  margin: 0;
  font-size: 13px;
  line-height: 1.8;
  color: var(--muted);
}

.about-copy p + p {
  margin-top: 12px;
}

.about-actions {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* —— FAQ —— */
.faq-list {
  margin-top: 12px;
}

.faq-item {
  border-bottom: 1px solid var(--line);
}

.faq-q {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border: none;
  background: none;
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.faq-icon {
  width: 10px;
  height: 10px;
  border-right: 1.5px solid var(--blue);
  border-bottom: 1.5px solid var(--blue);
  transform: rotate(45deg);
  transition: transform 0.25s ease;
  flex-shrink: 0;
}

.faq-icon.open {
  transform: rotate(225deg);
}

.faq-a {
  margin: 0;
  padding: 0 0 16px;
  font-size: 13px;
  line-height: 1.7;
  color: var(--muted);
}

/* —— Partners —— */
.partner-row {
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.partner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-motif);
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  background: rgba(10, 18, 27, 0.6);
  transition: border-color 0.2s ease, color 0.2s ease;
}

.partner:hover {
  border-color: rgba(27, 117, 188, 0.5);
  color: var(--ink);
}

@media (max-width: 390px) {
  .principles {
    grid-template-columns: 1fr;
  }

  .principle {
    border-right: none;
  }
}

@media (min-width: 769px) {
  .home-hl {
    padding-top: 20px;
  }

  .hero {
    min-height: 0;
    align-items: flex-start;
  }

  .hero-inner {
    padding: 48px 32px 48px;
  }

  .hero-mark {
    width: 300px;
    margin-bottom: 24px;
  }

  .hero-headline {
    max-width: 16ch;
    font-size: 48px;
  }

  .hero-lead {
    font-size: 15px;
    max-width: 40rem;
  }

  .band {
    padding: 56px 32px;
  }

  .principles {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    padding-left: 32px;
    padding-right: 32px;
  }

  .principle {
    padding: 28px 20px;
    border-bottom: none;
    border-right: 1px solid var(--line);
  }

  .principle:nth-child(2n) {
    border-right: 1px solid var(--line);
  }

  .principle:last-child {
    border-right: none;
  }

  .solution-list {
    grid-template-columns: 1fr 1fr;
  }

  .solution {
    padding: 22px 18px;
    border-right: 1px solid var(--line);
  }

  .solution:nth-child(2n) {
    border-right: none;
  }

  .timeline {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0;
  }

  .timeline-step {
    padding: 8px 24px 8px 18px;
    border-left: 2px solid rgba(27, 117, 188, 0.45);
  }
}
</style>
